import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  X,
  Save,
  Edit,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  Calendar,
  MapPin,
  Globe,
  Clock,
  AlertCircle,
  Loader2,
  TrendingUp,
  Briefcase,
  Trash2,
  ArrowUpDown,
  RotateCcw,
  Shield
} from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { enUS } from "date-fns/locale";
import DateFormatter from './DateFormatter';
import RichTextEditor from './RichTextEditor';
import { getAllJobs, createJob, updateJob, deleteJob, getJobById } from '../services/Jobsapi';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Grid,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Stack,
  Tooltip
} from '@mui/material';
import PageHeader from './headers/Header';

const Jobs = () => {
  const theme = useTheme();
  const [showForm, setShowForm] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [formData, setFormData] = useState({
    jobId: '',
    jobTitle: '',
    position: '',
    jobName: '',
    jobDescription: '',
    city: '',
    platform: [],
    timeline: '',
    status: 'draft',
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [editingJobId, setEditingJobId] = useState(null);

  const hasActiveFilters = Boolean(searchTerm || filterStatus !== 'all' || sortBy !== 'newest');

  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setSortBy('newest');
  };
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const formRef = useRef(null);
  const platformDropdownRef = useRef(null);

  const PLATFORMS = [
    { value: 'Marabes_Webiste', label: 'Marabes Website' },
    { value: 'Indeed', label: 'Indeed' },
    { value: 'LinkedIn', label: 'LinkedIn' }
  ];
  const CITIES = [
    { value: 'Tangier', label: 'Tangier' },
    { value: 'Nador', label: 'Nador' }
  ];
  const STATUSES = [
    { value: 'draft', label: 'Draft' },
    { value: 'posted', label: 'Posted' },
    { value: 'expired', label: 'Expired' }
  ];
  const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'title', label: 'Title A-Z' },
    { value: 'status', label: 'Status' }
  ];

  // Auto-dismiss notifications
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => setShowSuccess(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Close platform dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (platformDropdownRef.current && !platformDropdownRef.current.contains(event.target)) {
        setShowPlatformDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch jobs
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAllJobs();
        setJobs(data);
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
        setError('Failed to load jobs. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!formData.jobTitle?.trim()) errors.jobTitle = 'Job title is required';
    if (!formData.position?.trim()) errors.position = 'Position is required';
    if (!formData.jobName?.trim()) errors.jobName = 'Job name is required';
    const stripHtml = (html) => {
      if (!html) return '';
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    };
    
    const plainDescription = stripHtml(formData.jobDescription);
    if (!plainDescription.trim()) {
      errors.jobDescription = 'Job description is required';
    } else if (plainDescription.trim().length < 10) {
      errors.jobDescription = 'Description must be at least 10 characters';
    }
    if (!formData.city) errors.city = 'City is required';
    if (!formData.platform.length) errors.platform = 'At least one platform must be selected';
    if (formData.timeline) {
      const selectedDate = new Date(formData.timeline);
      if (isNaN(selectedDate.getTime())) {
        errors.timeline = 'Invalid date';
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) errors.timeline = 'Date cannot be in the past';
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const showNotification = (message, type = 'success') => {
    if (type === 'success') {
      setSuccessMessage(message);
      setShowSuccess(true);
    } else {
      setError(message);
    }
  };

  const handleAddJob = () => {
    setShowForm(true);
    setFormData({
      jobId: '',
      jobTitle: '',
      position: '',
      jobName: '',
      jobDescription: '',
      city: '',
      platform: [],
      timeline: '',
      status: 'draft',
    });
    setEditingJobId(null);
    setShowPlatformDropdown(false);
    setValidationErrors({});
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handlePlatformToggle = (platformValue) => {
    setFormData(prev => ({
      ...prev,
      platform: prev.platform.includes(platformValue)
        ? prev.platform.filter(p => p !== platformValue)
        : [...prev.platform, platformValue]
    }));
    if (validationErrors.platform) {
      setValidationErrors(prev => ({ ...prev, platform: null }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      // Keep error message minimal or rely on field errors
      return;
    }
    try {
      setSubmitting(true);
      const newJob = await createJob(formData);
      setJobs([...jobs, newJob]);
      setShowForm(false);
      setShowPlatformDropdown(false);
      setValidationErrors({});
      showNotification('Job created successfully!');
    } catch (error) {
      console.error('Failed to create job:', error);
      setError('Failed to create job. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateJob = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    try {
      setSubmitting(true);
      const updatedJob = await updateJob(formData.jobId, formData);
      setJobs(jobs.map(job => job.jobId === formData.jobId ? updatedJob : job));
      setShowForm(false);
      setEditingJobId(null);
      setShowPlatformDropdown(false);
      setValidationErrors({});
      showNotification('Job updated successfully!');
    } catch (error) {
      console.error('Failed to update job:', error);
      setError('Failed to update job. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditJob = async (job) => {
    setEditingJobId(job.jobId);
    try {
      // Optionally show a loader if fetching details is slow, 
      // but usually editing uses local data or fast fetch
      const jobData = await getJobById(job.jobId);
      setFormData({
        jobId: jobData.jobId,
        jobTitle: jobData.jobTitle,
        position: jobData.position,
        jobName: jobData.jobName,
        jobDescription: jobData.jobDescription,
        city: jobData.city,
        platform: Array.isArray(jobData.platform) ? jobData.platform : [jobData.platform],
        timeline: jobData.timeline || '',
        status: jobData.status,
      });
      setShowForm(true);
      setShowPlatformDropdown(false);
      setValidationErrors({});
    } catch (error) {
      console.error('Failed to fetch job:', error);
      setError('Failed to load job details.');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job? This cannot be undone.')) {
      try {
        setDeletingJobId(jobId);
        await deleteJob(jobId);
        setJobs(jobs.filter(job => job.jobId !== jobId));
        showNotification('Job deleted successfully!');
      } catch (error) {
        console.error('Failed to delete job:', error);
        setError('Failed to delete job. Please try again.');
      } finally {
        setDeletingJobId(null);
      }
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingJobId(null);
    setShowPlatformDropdown(false);
    setValidationErrors({});
  };

  const getFilteredAndSortedJobs = () => {
    let filtered = jobs;
    if (filterStatus !== 'all') {
      filtered = filtered.filter(job => job.status === filterStatus);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(job =>
        job.jobTitle?.toLowerCase().includes(term) ||
        job.position?.toLowerCase().includes(term) ||
        job.jobName?.toLowerCase().includes(term) ||
        job.jobDescription?.toLowerCase().includes(term) ||
        job.city?.toLowerCase().includes(term)
      );
    }
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdDate || 0) - new Date(a.createdDate || 0));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.createdDate || 0) - new Date(b.createdDate || 0));
        break;
      case 'title':
        filtered.sort((a, b) => a.jobTitle?.localeCompare(b.jobTitle || ''));
        break;
      case 'status':
        filtered.sort((a, b) => a.status?.localeCompare(b.status || ''));
        break;
      default:
        break;
    }
    return filtered;
  };

  const filteredJobs = getFilteredAndSortedJobs();

  const formatStatusDisplay = (status) => status.charAt(0).toUpperCase() + status.slice(1);
  const formatPlatformDisplay = (platform) => PLATFORMS.find(p => p.value === platform)?.label || platform;

  const getStatusColor = (status) => {
    switch (status) {
      case 'posted': return 'success';
      case 'expired': return 'error';
      default: return 'warning';
    }
  };

  return (
    <Box sx={{
      p: 3,
      minHeight: '100vh',
      bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#f8fafc'
    }}>
      {/* Notifications */}
      {showSuccess && (
        <Alert
          severity="success"
          onClose={() => setShowSuccess(false)}
          sx={{ mb: 2, position: 'fixed', top: 20, right: 20, zIndex: 2000, boxShadow: theme.shadows[4] }}
        >
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2, position: 'fixed', top: 20, right: 20, zIndex: 2000, boxShadow: theme.shadows[4] }}
        >
          {error}
        </Alert>
      )}

      {/* Header */}
      <PageHeader
        title="Job Listings"
        subtitle={`Manage and track job postings (${jobs.length} total)`}
        actions={
          <Button
            variant="contained"
            onClick={handleAddJob}
            disabled={loading}
            startIcon={<Plus size={18} />}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main || theme.palette.info.main})`,
              boxShadow: theme.shadows[3],
            }}
          >
            Add New Job
          </Button>
        }
      />

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(12px)',
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <div className="axo-search-container">
              <div className="axo-search-icon">
                <Search size={18} />
              </div>
              <input
                type="text"
                className="axo-search-input"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </Grid>
          <Grid item xs={12} md={4}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
              <Shield
                size={16}
                style={{
                  position: 'absolute',
                  left: '12px',
                  color: '#1976d2',
                  pointerEvents: 'none',
                  zIndex: 1,
                  flexShrink: 0
                }}
              />
              <select
                className="axo-filter-select"
                style={{
                  width: '100%',
                  paddingLeft: '38px',
                  borderColor: filterStatus !== 'all' ? '#1976d2' : undefined,
                  fontWeight: filterStatus !== 'all' ? 600 : undefined
                }}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </Grid>
          <Grid item xs={12} md={4}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
              <ArrowUpDown
                size={16}
                style={{
                  position: 'absolute',
                  left: '12px',
                  color: '#1976d2',
                  pointerEvents: 'none',
                  zIndex: 1,
                  flexShrink: 0
                }}
              />
              <select
                className="axo-filter-select"
                style={{
                  width: '100%',
                  paddingLeft: '38px',
                  borderColor: sortBy !== 'newest' ? '#1976d2' : undefined,
                  fontWeight: sortBy !== 'newest' ? 600 : undefined
                }}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" fontWeight="600">
            Showing <Box component="span" sx={{ color: 'primary.main' }}>{filteredJobs.length}</Box> of {jobs.length} jobs
          </Typography>
          {hasActiveFilters && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<RotateCcw size={14} />}
              onClick={resetFilters}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Reset Filters
            </Button>
          )}
        </Box>
      </Paper>

      {/* Jobs List */}
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
          <CircularProgress size={40} />
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>Loading jobs...</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredJobs.length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{
                textAlign: 'center',
                py: 8,
                bgcolor: alpha(theme.palette.background.paper, 0.5),
                borderRadius: 2,
                border: `1px dashed ${theme.palette.divider}`
              }}>
                <TrendingUp size={48} color={theme.palette.text.disabled} style={{ marginBottom: 16 }} />
                <Typography variant="h6" color="text.secondary">No jobs found</Typography>
                <Typography variant="body2" color="text.disabled">Try adjusting your filters</Typography>
              </Box>
            </Grid>
          ) : (
            filteredJobs.map((job) => (
              <Grid item xs={12} md={6} lg={4} key={job.jobId}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    borderLeft: `4px solid ${job.status === 'posted' ? theme.palette.success.main :
                      job.status === 'expired' ? theme.palette.error.main :
                        theme.palette.warning.main
                      }`,
                    bgcolor: 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[4]
                    },
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" fontWeight="700" color="text.primary" sx={{ lineHeight: 1.3 }}>
                      {job.jobTitle}
                    </Typography>
                    <Chip
                      label={formatStatusDisplay(job.status)}
                      size="small"
                      color={getStatusColor(job.status)}
                      icon={
                        job.status === 'posted' ? <CheckCircle size={14} /> :
                          job.status === 'expired' ? <XCircle size={14} /> :
                            <Clock size={14} />
                      }
                      sx={{ fontWeight: '600' }}
                    />
                  </Box>

                  <Box sx={{ mb: 2, display: 'flex', gap: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MapPin size={14} />
                      {job.city}
                    </Box>
                    {job.timeline && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Calendar size={14} />
                        <DateFormatter date={job.timeline} />
                      </Box>
                    )}
                  </Box>

                  <Box 
                    sx={{ mb: 2, flexGrow: 1, lineHeight: 1.6, fontSize: '0.875rem', color: 'text.primary', '& p': { margin: 0, padding: 0 } }}
                    dangerouslySetInnerHTML={{ __html: job.jobDescription }} 
                  />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                    {(Array.isArray(job.platform) ? job.platform : [job.platform]).map((p, i) => (
                      <Chip
                        key={i}
                        label={formatPlatformDisplay(p)}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          fontWeight: '600',
                          fontSize: '0.75rem'
                        }}
                      />
                    ))}
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 'auto' }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditJob(job)}
                        sx={{ border: `1px solid ${theme.palette.divider}` }}
                      >
                        <Edit size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteJob(job.jobId)}
                        disabled={deletingJobId === job.jobId}
                        sx={{ border: `1px solid ${theme.palette.error.light}`, bgcolor: alpha(theme.palette.error.main, 0.05) }}
                      >
                        {deletingJobId === job.jobId ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Form Overlay — same pattern as Employees.js */}
      {showForm && (
        <div className="form-overlay" onClick={handleCloseForm}>
          <div className="form-modal" onClick={e => e.stopPropagation()} style={{ padding: '2rem' }}>
            <button
              onClick={handleCloseForm}
              style={{
                position: 'absolute',
                top: '1.2rem',
                right: '1.2rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6B7280',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={20} />
            </button>
            <div className="form-section" style={{ padding: '0 0.5rem' }}>
              <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: theme.palette.primary.main, marginBottom: '1.5rem' }}>
                {editingJobId ? 'Edit Job' : 'Add New Job'}
              </h2>
              <form onSubmit={editingJobId ? handleUpdateJob : handleFormSubmit}>
                <div className="uf-form-section">
                  <div className="uf-form-grid">
                    <div className="uf-form-group">
                      <label className="uf-form-label">Job Title <span className="required">*</span></label>
                      <input
                        type="text"
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleFormChange}
                        className={`uf-form-input ${validationErrors.jobTitle ? 'error' : ''}`}
                        required
                      />
                      {validationErrors.jobTitle && <div className="uf-form-error-msg">{validationErrors.jobTitle}</div>}
                    </div>

                    <div className="uf-form-group">
                      <label className="uf-form-label">Position <span className="required">*</span></label>
                      <input
                        type="text"
                        name="position"
                        value={formData.position}
                        onChange={handleFormChange}
                        className={`uf-form-input ${validationErrors.position ? 'error' : ''}`}
                        required
                      />
                      {validationErrors.position && <div className="uf-form-error-msg">{validationErrors.position}</div>}
                    </div>

                    <div className="uf-form-group">
                      <label className="uf-form-label">Job Name (Internal) <span className="required">*</span></label>
                      <input
                        type="text"
                        name="jobName"
                        value={formData.jobName}
                        onChange={handleFormChange}
                        className={`uf-form-input ${validationErrors.jobName ? 'error' : ''}`}
                        required
                      />
                      {validationErrors.jobName && <div className="uf-form-error-msg">{validationErrors.jobName}</div>}
                    </div>

                    <div className="uf-form-group">
                      <label className="uf-form-label">City <span className="required">*</span></label>
                      <select
                        name="city"
                        value={formData.city}
                        onChange={handleFormChange}
                        className={`uf-form-select ${validationErrors.city ? 'error' : ''}`}
                        required
                      >
                        <option value="">Select City</option>
                        {CITIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                      {validationErrors.city && <div className="uf-form-error-msg">{validationErrors.city}</div>}
                    </div>

                    <div className="uf-form-group full-width">
                      <label className="uf-form-label">Job Description <span className="required">*</span></label>
                      <RichTextEditor
                        value={formData.jobDescription || ''}
                        onChange={(val) => {
                          setFormData(prev => ({ ...prev, jobDescription: val }));
                          if (validationErrors.jobDescription) {
                            setValidationErrors(prev => ({ ...prev, jobDescription: null }));
                          }
                        }}
                      />
                      {validationErrors.jobDescription && <div className="uf-form-error-msg">{validationErrors.jobDescription || "Minimum 10 characters"}</div>}
                    </div>

                    <div className="uf-form-group">
                      <label className="uf-form-label">Platforms</label>
                      <div style={{ position: 'relative' }} ref={platformDropdownRef}>
                        <div
                          onClick={() => setShowPlatformDropdown(!showPlatformDropdown)}
                          style={{
                            padding: '0.45rem 0.875rem',
                            border: `1.5px solid ${validationErrors.platform ? '#ef4444' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0')}`,
                            borderRadius: '0.375rem',
                            cursor: 'pointer',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.25rem',
                            minHeight: '44px',
                            alignItems: 'center',
                            backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff'
                          }}
                        >
                          {formData.platform.length === 0 ? (
                            <span style={{ color: theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b', fontSize: '0.875rem' }}>Select platforms...</span>
                          ) : (
                            formData.platform.map(p => (
                              <Chip
                                key={p}
                                label={formatPlatformDisplay(p)}
                                size="small"
                                onDelete={(e) => {
                                  e.stopPropagation();
                                  handlePlatformToggle(p);
                                }}
                                sx={{
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  color: 'primary.main',
                                  fontWeight: '600',
                                  fontSize: '0.75rem',
                                  height: '24px'
                                }}
                              />
                            ))
                          )}
                        </div>
                        {showPlatformDropdown && (
                          <Paper
                            elevation={8}
                            sx={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              zIndex: 10,
                              mt: 1,
                              maxHeight: 200,
                              overflowY: 'auto',
                              bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff'
                            }}
                          >
                            {PLATFORMS.map(p => (
                              <MenuItem
                                key={p.value}
                                onClick={() => handlePlatformToggle(p.value)}
                                selected={formData.platform.includes(p.value)}
                                sx={{ fontSize: '0.875rem' }}
                              >
                                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                                  {p.label}
                                  {formData.platform.includes(p.value) && <CheckCircle size={16} color={theme.palette.primary.main} />}
                                </Box>
                              </MenuItem>
                            ))}
                          </Paper>
                        )}
                        {validationErrors.platform && <div className="uf-form-error-msg">{validationErrors.platform}</div>}
                      </div>
                    </div>

                    <div className="uf-form-group">
                      <label className="uf-form-label">Timeline</label>
                      <input
                        type="date"
                        name="timeline"
                        value={formData.timeline ? new Date(formData.timeline).toISOString().split('T')[0] : ''}
                        onChange={handleFormChange}
                        className={`uf-form-input ${validationErrors.timeline ? 'error' : ''}`}
                      />
                      {validationErrors.timeline && <div className="uf-form-error-msg">{validationErrors.timeline}</div>}
                    </div>

                    <div className="uf-form-group">
                      <label className="uf-form-label">Status</label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleFormChange}
                        className="uf-form-select"
                      >
                        {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="uf-form-actions">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseForm}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? (
                      <><Loader2 size={16} className="animate-spin" style={{ marginRight: '8px' }} /> Saving...</>
                    ) : (editingJobId ? 'Update Job' : 'Create Job')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <style jsx global>{`
        .react-datepicker-wrapper {
          width: 100%;
        }
      `}</style>
    </Box>
  );
};

export default Jobs;